import { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import {
  ArrowsLeftRight, Plus, X, CheckCircle, Sparkle, UserCircle,
  ClipboardText, CurrencyDollar, Wrench, Package,
} from "@phosphor-icons/react";

const fmt = (n) => new Intl.NumberFormat("es-AR", { minimumFractionDigits: 2 }).format(n || 0);

const EMPLOYEES = ["Yesi", "Cris"];

const DEFAULT_CHECKLIST = [
  "Caja arqueada y contada",
  "Stock crítico revisado",
  "Reparaciones pendientes informadas",
  "Billeteras y fiados al día",
  "Local ordenado y limpio",
];

const fmtDateTime = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("es-AR", { timeZone: "America/Argentina/Buenos_Aires", dateStyle: "medium", timeStyle: "short" });
  } catch (_e) { return iso; }
};

function EmployeeField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono mb-1.5">{label}</label>
      <div className="flex gap-2 mb-2">
        {EMPLOYEES.map((name) => (
          <button
            key={name}
            type="button"
            onClick={() => onChange(name)}
            data-testid={`${testid}-chip-${name.toLowerCase()}`}
            className={`px-3 py-1 rounded-full text-xs font-semibold border transition ${
              value === name
                ? "border-turquoise bg-turquoise/15 text-turquoise"
                : "border-white/10 text-zinc-400 hover:border-white/25"
            }`}
          >
            {name}
          </button>
        ))}
      </div>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={`${testid}-input`}
        placeholder="O escribí otro nombre..."
        className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm outline-none focus:border-turquoise/50 transition"
      />
    </div>
  );
}

export default function ShiftHandover() {
  const [handovers, setHandovers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [outgoing, setOutgoing] = useState("");
  const [incoming, setIncoming] = useState("");
  const [supervisor, setSupervisor] = useState("");
  const [notes, setNotes] = useState("");
  const [checklist, setChecklist] = useState(DEFAULT_CHECKLIST.map((label) => ({ label, done: false })));
  const [newItem, setNewItem] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [lastConfirmed, setLastConfirmed] = useState(null);

  const load = useCallback(() => {
    api.get("/shift-handovers").then(({ data }) => setHandovers(data)).catch(() => {});
    api.get("/shift-handovers/summary-preview").then(({ data }) => setSummary(data)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggleItem = (idx) =>
    setChecklist((prev) => prev.map((it, i) => (i === idx ? { ...it, done: !it.done } : it)));

  const removeItem = (idx) => setChecklist((prev) => prev.filter((_, i) => i !== idx));

  const addItem = () => {
    const label = newItem.trim();
    if (!label) return;
    setChecklist((prev) => [...prev, { label, done: false }]);
    setNewItem("");
  };

  const doneCount = checklist.filter((c) => c.done).length;

  const submit = async () => {
    if (!outgoing.trim() || !incoming.trim()) {
      toast.error("Completá el empleado saliente y el entrante");
      return;
    }
    setSubmitting(true);
    try {
      const { data } = await api.post("/shift-handovers", {
        outgoing_employee: outgoing,
        incoming_employee: incoming,
        supervisor,
        notes,
        checklist,
      });
      setLastConfirmed(data);
      toast.success("Relevo confirmado", { description: data.motivational_message });
      setOutgoing("");
      setIncoming("");
      setSupervisor("");
      setNotes("");
      setChecklist(DEFAULT_CHECKLIST.map((label) => ({ label, done: false })));
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "No se pudo registrar el relevo");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="p-6 md:p-10" data-testid="shift-handover-page">
      <header className="mb-6">
        <p className="text-xs uppercase tracking-[0.3em] text-turquoise font-mono mb-1">Operaciones</p>
        <h1 className="text-3xl md:text-4xl font-heading font-bold flex items-center gap-3">
          <ArrowsLeftRight size={30} className="text-turquoise" /> Cambio de Turno
        </h1>
        <p className="text-zinc-400 mt-1 text-sm">Relevo de puesto con trazabilidad completa</p>
      </header>

      {/* Resumen automático del turno */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="shift-summary">
          <div className="bg-[#0A0A0A] border border-white/5 rounded-lg p-4">
            <div className="flex items-center gap-2 text-zinc-500 text-[10px] uppercase tracking-widest font-mono mb-1">
              <CurrencyDollar size={14} /> Ventas de hoy
            </div>
            <div className="text-xl font-heading font-bold text-turquoise font-mono">${fmt(summary.today_total)}</div>
            <div className="text-[11px] text-zinc-500">{summary.today_count} operaciones</div>
          </div>
          <div className="bg-[#0A0A0A] border border-white/5 rounded-lg p-4">
            <div className="flex items-center gap-2 text-zinc-500 text-[10px] uppercase tracking-widest font-mono mb-1">
              <Wrench size={14} /> Reparaciones
            </div>
            <div className="text-xl font-heading font-bold">{summary.pending_repairs} en curso</div>
            <div className="text-[11px] text-amber-400">{summary.ready_repairs} listas para retirar</div>
          </div>
          <div className="bg-[#0A0A0A] border border-white/5 rounded-lg p-4">
            <div className="flex items-center gap-2 text-zinc-500 text-[10px] uppercase tracking-widest font-mono mb-1">
              <Package size={14} /> Stock bajo
            </div>
            <div className="text-xl font-heading font-bold">{summary.low_stock}</div>
            <div className="text-[11px] text-zinc-500">productos en mínimo</div>
          </div>
          <div className="bg-[#0A0A0A] border border-white/5 rounded-lg p-4">
            <div className="flex items-center gap-2 text-zinc-500 text-[10px] uppercase tracking-widest font-mono mb-1">
              <ClipboardText size={14} /> Checklist
            </div>
            <div className="text-xl font-heading font-bold font-mono">{doneCount}/{checklist.length}</div>
            <div className="text-[11px] text-zinc-500">ítems completados</div>
          </div>
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-6 items-start">
        {/* Formulario de relevo */}
        <section className="bg-[#0A0A0A] border border-white/5 rounded-xl p-5 md:p-6 space-y-5" data-testid="handover-form">
          <h2 className="font-heading font-bold text-lg">Nuevo relevo</h2>

          <div className="grid sm:grid-cols-2 gap-4">
            <EmployeeField label="Empleado saliente" value={outgoing} onChange={setOutgoing} testid="outgoing-employee" />
            <EmployeeField label="Empleado entrante" value={incoming} onChange={setIncoming} testid="incoming-employee" />
          </div>

          <EmployeeField label="Supervisor (opcional)" value={supervisor} onChange={setSupervisor} testid="supervisor" />

          {/* Checklist */}
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono mb-2">
              Checklist operativo
            </label>
            <div className="space-y-2" data-testid="checklist">
              {checklist.map((item, idx) => (
                <div
                  key={idx}
                  className={`flex items-center gap-3 border rounded-md px-3 py-2 transition ${
                    item.done ? "border-turquoise/30 bg-turquoise/5" : "border-white/10 bg-white/[0.02]"
                  }`}
                >
                  <button
                    type="button"
                    onClick={() => toggleItem(idx)}
                    data-testid={`checklist-toggle-${idx}`}
                    className={`w-5 h-5 rounded flex items-center justify-center border transition flex-shrink-0 ${
                      item.done ? "bg-turquoise border-turquoise text-black" : "border-white/20 hover:border-turquoise/50"
                    }`}
                    aria-label={`Marcar ${item.label}`}
                  >
                    {item.done && <CheckCircle size={14} weight="bold" />}
                  </button>
                  <span className={`flex-1 text-sm ${item.done ? "text-zinc-400 line-through" : ""}`}>{item.label}</span>
                  <button
                    type="button"
                    onClick={() => removeItem(idx)}
                    data-testid={`checklist-remove-${idx}`}
                    className="text-zinc-600 hover:text-red-400 transition"
                    aria-label={`Quitar ${item.label}`}
                  >
                    <X size={14} />
                  </button>
                </div>
              ))}
            </div>
            <div className="flex gap-2 mt-2">
              <input
                value={newItem}
                onChange={(e) => setNewItem(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && addItem()}
                data-testid="checklist-new-item-input"
                placeholder="Agregar ítem personalizado..."
                className="flex-1 bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm outline-none focus:border-turquoise/50 transition"
              />
              <button
                type="button"
                onClick={addItem}
                data-testid="checklist-add-btn"
                className="px-3 py-2 rounded-md border border-white/10 hover:border-turquoise/50 text-turquoise transition"
              >
                <Plus size={16} weight="bold" />
              </button>
            </div>
          </div>

          {/* Notas */}
          <div>
            <label className="block text-[10px] uppercase tracking-[0.2em] text-zinc-500 font-mono mb-1.5">
              Notas para el turno entrante
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              data-testid="handover-notes-input"
              rows={3}
              placeholder="Pendientes, encargos, avisos importantes..."
              className="w-full bg-white/5 border border-white/10 rounded-md px-3 py-2 text-sm outline-none focus:border-turquoise/50 transition resize-none"
            />
          </div>

          <button
            onClick={submit}
            disabled={submitting}
            data-testid="confirm-handover-btn"
            className="w-full bg-brand-gradient text-black font-semibold py-3 rounded-md flex items-center justify-center gap-2 shadow-cyan-glow hover:opacity-90 transition disabled:opacity-50"
          >
            <CheckCircle size={18} weight="bold" />
            {submitting ? "Registrando..." : "Confirmar relevo"}
          </button>

          {/* Confirmación con sello de fecha/hora */}
          {lastConfirmed && (
            <div className="border border-turquoise/30 bg-turquoise/5 rounded-lg p-4" data-testid="handover-confirmation">
              <div className="flex items-center gap-2 text-turquoise font-semibold text-sm mb-1">
                <UserCircle size={18} weight="duotone" /> Relevo registrado
              </div>
              <p className="text-sm text-zinc-300">
                <span className="text-white font-medium">{lastConfirmed.outgoing_employee}</span>
                {" → "}
                <span className="text-white font-medium">{lastConfirmed.incoming_employee}</span>
              </p>
              <p className="text-xs text-zinc-500 font-mono mt-1">{fmtDateTime(lastConfirmed.confirmed_at)}</p>
              <p className="text-sm text-turquoise/90 mt-3 flex items-start gap-2">
                <Sparkle size={16} className="flex-shrink-0 mt-0.5" weight="fill" />
                <span className="italic">{lastConfirmed.motivational_message}</span>
              </p>
            </div>
          )}
        </section>

        {/* Historial */}
        <section data-testid="handover-history">
          <h2 className="font-heading font-bold text-lg mb-3">Historial de relevos</h2>
          {handovers.length === 0 ? (
            <p className="text-sm text-zinc-500 border border-dashed border-white/10 rounded-lg p-6 text-center">
              Todavía no hay relevos registrados.
            </p>
          ) : (
            <div className="space-y-3">
              {handovers.map((h) => {
                const done = (h.checklist || []).filter((c) => c.done).length;
                return (
                  <article
                    key={h.handover_id}
                    data-testid={`handover-card-${h.handover_id}`}
                    className="bg-[#0A0A0A] border border-white/5 rounded-lg p-4 hover:border-white/10 transition"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <p className="font-medium text-sm">
                          {h.outgoing_employee}
                          <ArrowsLeftRight size={12} className="inline mx-1.5 text-turquoise" />
                          {h.incoming_employee}
                        </p>
                        <p className="text-[11px] text-zinc-500 font-mono mt-0.5">{fmtDateTime(h.confirmed_at)}</p>
                      </div>
                      <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-1 rounded-full border border-turquoise/20 bg-turquoise/10 text-turquoise flex-shrink-0">
                        {done}/{(h.checklist || []).length} checklist
                      </span>
                    </div>
                    {h.supervisor && (
                      <p className="text-xs text-zinc-400 mt-2">Supervisor: <span className="text-zinc-200">{h.supervisor}</span></p>
                    )}
                    {h.notes && <p className="text-sm text-zinc-300 mt-2 whitespace-pre-wrap">{h.notes}</p>}
                    {h.motivational_message && (
                      <p className="text-xs text-turquoise/80 italic mt-2 flex items-start gap-1.5">
                        <Sparkle size={12} className="flex-shrink-0 mt-0.5" weight="fill" />
                        {h.motivational_message}
                      </p>
                    )}
                    <p className="text-[10px] text-zinc-600 mt-2 font-mono">Registrado por {h.created_by}</p>
                  </article>
                );
              })}
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
